import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import BookCard from "../components/BookCard";
import CreateCategoryDialog from "../components/CreateCategoryDialog";
import { BookOpenIcon, SearchIcon } from "../components/Icon";
import ManageCategoriesDialog from "../components/ManageCategoriesDialog";
import UrlInput from "../components/UrlInput";
import { useBooks } from "../hooks/useBooks";
import { useCategories } from "../hooks/useCategories";
import type { BookStatus, Category } from "../api/types";

/** Sentinel values for the category chip row; anything else is a real category id. */
const ALL_CATEGORIES = "";
const UNCATEGORIZED = "none";

const STATUS_OPTIONS: { value: BookStatus | ""; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "queued", label: "Queued" },
  { value: "processing", label: "Processing" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "canceled", label: "Canceled" },
];

export default function Library() {
  const [status, setStatus] = useState<BookStatus | "">("");
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(ALL_CATEGORIES);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [showManageCategories, setShowManageCategories] = useState(false);
  const queryClient = useQueryClient();

  const { data: categoriesData } = useCategories();
  const categories = categoriesData?.categories ?? [];

  useEffect(() => {
    const handle = setTimeout(() => setQ(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // If the selected category was deleted out from under us (e.g. via the manage dialog),
  // fall back to "All" instead of silently showing a filter for a category that no longer exists.
  useEffect(() => {
    if (!categoriesData) return;
    if (
      categoryFilter !== ALL_CATEGORIES &&
      categoryFilter !== UNCATEGORIZED &&
      !categoriesData.categories.some((c) => c.id === categoryFilter)
    ) {
      setCategoryFilter(ALL_CATEGORIES);
    }
  }, [categoriesData, categoryFilter]);

  const params = useMemo(
    () => ({ status: status || undefined, q: q || undefined, limit: 60, category: categoryFilter || undefined }),
    [status, q, categoryFilter],
  );
  const { data, isLoading, isError } = useBooks(params);

  const books = data?.books ?? [];
  const hasFilters = !!status || !!q || categoryFilter !== ALL_CATEGORIES;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className="mb-10 max-w-2xl">
        <h1 className="font-serif text-3xl font-semibold text-text">Your library</h1>
        <p className="mt-2 text-muted">
          Paste a video link and Boort rewrites the full transcript into a properly typeset book -
          every fact and story kept, the filler gone - ready as EPUB, PDF, or Markdown.
        </p>
        <div className="mt-5">
          <UrlInput
            autoFocus
            onCreated={() => void queryClient.invalidateQueries({ queryKey: ["books"] })}
          />
        </div>
      </div>

      <CategoryChips
        categories={categories}
        selected={categoryFilter}
        onSelect={setCategoryFilter}
        onNew={() => setShowCreateCategory(true)}
        onManage={() => setShowManageCategories(true)}
      />

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-xs flex-1">
          <SearchIcon
            width={16}
            height={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
          />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search title or channel…"
            className="w-full rounded-md border border-border bg-surface py-2 pl-9 pr-3 text-sm text-text placeholder:text-muted"
            aria-label="Search books"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as BookStatus | "")}
          className="rounded-md border border-border bg-surface pl-3 pr-9 py-2 text-sm text-text"
          aria-label="Filter by status"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] animate-pulse rounded-lg bg-surface" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-danger">Could not load your library. Is the backend running?</p>
      ) : books.length === 0 ? (
        hasFilters ? (
          <p className="py-16 text-center text-muted">No books match your search.</p>
        ) : (
          <EmptyState />
        )
      ) : (
        <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
          {books.map((book) => (
            <BookCard key={book.id} book={book} />
          ))}
        </div>
      )}

      <CreateCategoryDialog
        open={showCreateCategory}
        onClose={() => setShowCreateCategory(false)}
        onCreated={(id) => setCategoryFilter(id)}
      />
      <ManageCategoriesDialog
        open={showManageCategories}
        categories={categories}
        onClose={() => setShowManageCategories(false)}
      />
    </div>
  );
}

function CategoryChips({
  categories,
  selected,
  onSelect,
  onNew,
  onManage,
}: {
  categories: Category[];
  selected: string;
  onSelect: (value: string) => void;
  onNew: () => void;
  onManage: () => void;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-center gap-2">
      <CategoryChip label="All" active={selected === ALL_CATEGORIES} onClick={() => onSelect(ALL_CATEGORIES)} />
      {categories.map((category) => (
        <CategoryChip
          key={category.id}
          label={category.name}
          count={category.book_count}
          active={selected === category.id}
          onClick={() => onSelect(category.id)}
        />
      ))}
      <CategoryChip label="Uncategorized" active={selected === UNCATEGORIZED} onClick={() => onSelect(UNCATEGORIZED)} />
      <button
        type="button"
        onClick={onNew}
        className="rounded-full border border-dashed border-border px-3 py-1.5 text-xs font-medium text-muted hover:border-accent hover:text-accent"
      >
        + New
      </button>
      <button
        type="button"
        onClick={onManage}
        className="ml-1 text-xs font-medium text-muted underline decoration-border underline-offset-2 hover:text-text hover:decoration-text"
      >
        Manage
      </button>
    </div>
  );
}

function CategoryChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors " +
        (active ? "border-accent bg-accent-soft text-accent-strong" : "border-border bg-surface text-text hover:border-muted")
      }
    >
      {label}
      {count != null ? (
        <span
          className={
            "rounded-full px-1.5 py-0.5 text-[10px] leading-none " +
            (active ? "bg-accent/20 text-accent-strong" : "bg-bg text-muted")
          }
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border py-20 text-center">
      <BookOpenIcon width={28} height={28} className="text-muted" />
      <p className="max-w-sm text-muted">
        No books yet. Boort turns a YouTube video's transcript into a small, well-typeset book -
        every fact and story preserved, the filler gone. Paste a link above to start your first one.
      </p>
    </div>
  );
}
